import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { authService } from '../../../services/api';
import { ChevronDown } from 'lucide-react';
import DateRangePicker from '../../../components/DateRangePicker';

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'N/A';
    const pad = (num) => String(num).padStart(2, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  } catch (err) {
    return 'N/A';
  }
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const ManageUsersTab = ({ toast }) => {
  const { hasPermission } = useAuth();

  // Categories and pagination states
  const [userCategory, setUserCategory] = useState('admins'); // 'admins', 'users_vendors'
  const [userFilters, setUserFilters] = useState({ startDate: '', endDate: '' });
  const [userPage, setUserPage] = useState(0);
  const [userPageSize, setUserPageSize] = useState(25);

  // Data states
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create admin modal states
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [adminForm, setAdminForm] = useState({
    name: '',
    emailId: '',
    mobileNo: '',
    password: '',
    roles: []
  });

  // Edit user roles states
  const [editUserRoles, setEditUserRoles] = useState(null);

  // Password change states
  const [changePasswordUser, setChangePasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Delete user states
  const [deleteUserConfirm, setDeleteUserConfirm] = useState(null);
  const [deleteInputConfirm, setDeleteInputConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!hasPermission('MANAGE_USERS')) return;
    setLoading(true);
    setError('');
    try {
      const res = await authService.getAdminUsers();
      if (res.success) {
        setUsers(res.data || []);
      } else {
        setError(res.error || 'Failed to fetch users registry.');
      }
    } catch (err) {
      setError('An error occurred while fetching users.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateAdminSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (adminForm.roles.length === 0) {
      toast.warning('Please select at least one role.');
      return;
    }

    try {
      const res = await authService.adminCreateUser(adminForm);
      if (res.success) {
        toast.success(`Admin user ${adminForm.emailId} created successfully.`);
        setAdminForm({ name: '', emailId: '', mobileNo: '', password: '', roles: [] });
        setShowCreateAdminModal(false);
        fetchUsers();
      } else {
        toast.error("Failed to create admin: " + res.error);
      }
    } catch (err) {
      toast.error("An error occurred while creating admin user.");
      console.error(err);
    }
  };

  const handleRoleCheckboxChange = (role) => {
    const isChecked = adminForm.roles.includes(role);
    if (isChecked) {
      setAdminForm({ ...adminForm, roles: adminForm.roles.filter(r => r !== role) });
    } else {
      setAdminForm({ ...adminForm, roles: [...adminForm.roles, role] });
    }
  };

  const handleEditUserRolesSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const res = await authService.updateUserRoles(editUserRoles.email, editUserRoles.roles);
      if (res.success) {
        toast.success(`User roles updated successfully.`);
        setEditUserRoles(null);
        fetchUsers();
      } else {
        toast.error("Failed to update user roles: " + res.error);
      }
    } catch (err) {
      toast.error("An error occurred while updating user roles.");
      console.error(err);
    }
  };

  const handleEditUserRoleCheckboxChange = (role) => {
    const isChecked = editUserRoles.roles.includes(role);
    if (isChecked) {
      setEditUserRoles({ ...editUserRoles, roles: editUserRoles.roles.filter(r => r !== role) });
    } else {
      setEditUserRoles({ ...editUserRoles, roles: [...editUserRoles.roles, role] });
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      toast.warning('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    try {
      const res = await authService.updateUserPassword(changePasswordUser.email, newPassword);
      if (res.success) {
        toast.success(`Password updated and notification email sent to ${changePasswordUser.email} successfully.`);
        setChangePasswordUser(null);
        setNewPassword('');
        setConfirmNewPassword('');
        fetchUsers();
      } else {
        toast.error("Failed to update password: " + res.error);
      }
    } catch (err) {
      toast.error("An error occurred while updating user password.");
      console.error(err);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteUserSubmit = async (e) => {
    e.preventDefault();
    if (!deleteUserConfirm) return;
    if (deleteInputConfirm !== 'DELETE') {
      toast.error("Please type 'DELETE' to confirm deletion");
      return;
    }
    setDeleteLoading(true);
    try {
      const res = await authService.adminDeleteUser(deleteUserConfirm.id);
      if (res.success) {
        toast.success(res.message || "User and associated data deleted successfully");
        setDeleteUserConfirm(null);
        setDeleteInputConfirm('');
        fetchUsers();
      } else {
        toast.error(res.message || "Failed to delete user");
      }
    } catch (err) {
      toast.error(err.message || "Error deleting user");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-24 select-none">
        <div className="w-9 h-9 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-sm text-gray-500 font-semibold mt-3">Loading users directory...</span>
      </div>
    );
  }

  // Filter users by date range first
  const filteredUsersByDate = users.filter(u => {
    if (!u.createdAt) return true;
    const itemDateStr = u.createdAt.substring(0, 10);
    if (userFilters.startDate && itemDateStr < userFilters.startDate) return false;
    if (userFilters.endDate && itemDateStr > userFilters.endDate) return false;
    return true;
  });

  // Filter & sort admin vs vendor/client accounts
  const adminUsers = filteredUsersByDate.filter(u =>
    u.roles && u.roles.some(role =>
      ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_SALES_ADMIN', 'ROLE_LISTING_ADMIN', 'ROLE_SUPPORT_ADMIN', 'ROLE_MAGAZINE_SALES'].includes(role)
    )
  ).sort((a, b) => {
    const da = a.createdAt ? new Date(a.createdAt) : 0;
    const db = b.createdAt ? new Date(b.createdAt) : 0;
    return db - da;
  });

  const vendorOrClientUsers = filteredUsersByDate.filter(u =>
    !u.roles || !u.roles.some(role =>
      ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_SALES_ADMIN', 'ROLE_LISTING_ADMIN', 'ROLE_SUPPORT_ADMIN', 'ROLE_MAGAZINE_SALES'].includes(role)
    )
  ).sort((a, b) => {
    const da = a.createdAt ? new Date(a.createdAt) : 0;
    const db = b.createdAt ? new Date(b.createdAt) : 0;
    return db - da;
  });

  const activeUserList = userCategory === 'admins' ? adminUsers : vendorOrClientUsers;
  const userTotalPages = Math.ceil(activeUserList.length / userPageSize);
  const safeUserPage = Math.min(userPage, Math.max(0, userTotalPages - 1));
  const displayedUserList = activeUserList.slice(safeUserPage * userPageSize, (safeUserPage + 1) * userPageSize);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-left">
      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200/50 p-4 rounded-xl mb-6 flex items-center gap-3 shadow-sm select-none">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      <div className="flex justify-between items-center mb-6 gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">User Audit & Roles</h2>
        </div>
        <button
          onClick={() => setShowCreateAdminModal(true)}
          className="bg-[#2ab391] hover:bg-[#23977a] text-white text-xs sm:text-sm font-semibold py-1.5 px-3 sm:py-2 sm:px-4 rounded-lg shadow-md shadow-[#2ab391]/10 active:scale-[0.98] transition-all flex-shrink-0 cursor-pointer"
        >
          Add System Admin
        </button>
      </div>

      {/* User Category Sub-tabs & Date Filter */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center border-b border-gray-200 mb-6 gap-4 select-none">
        <div className="flex gap-2 sm:gap-6">
          <button
            onClick={() => {
              setUserCategory('admins');
              setUserPage(0);
            }}
            className={`pb-3 px-4 text-xs sm:text-sm font-bold border-b-2 transition-all cursor-pointer ${userCategory === 'admins'
              ? 'border-[#2ab391] text-[#2ab391]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            Administrative Accounts ({adminUsers.length})
          </button>
          <button
            onClick={() => {
              setUserCategory('users_vendors');
              setUserPage(0);
            }}
            className={`pb-3 px-4 text-xs sm:text-sm font-bold border-b-2 transition-all cursor-pointer ${userCategory === 'users_vendors'
              ? 'border-[#2ab391] text-[#2ab391]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            User & Vendor Accounts ({vendorOrClientUsers.length})
          </button>
        </div>

        {/* Date Range Picker */}
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 p-1.5 px-3 rounded-xl select-none mb-3 md:mb-1">
          <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">DATE:</span>
          <DateRangePicker
            startDate={userFilters.startDate}
            endDate={userFilters.endDate}
            onChange={(start, end) => {
              setUserFilters({ startDate: start, endDate: end });
              setUserPage(0);
            }}
            placeholder="Select Date Range"
            className="!w-48 text-xs"
          />
          {(userFilters.startDate || userFilters.endDate) && (
            <button
              onClick={() => {
                setUserFilters({ startDate: '', endDate: '' });
                setUserPage(0);
              }}
              className="px-2 py-0.5 text-xs font-semibold text-red-600 hover:bg-red-50 border border-red-200/50 rounded-lg transition-colors cursor-pointer"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">
              <th className="p-4 pl-6">User Name</th>
              <th className="p-4">Email ID</th>
              <th className="p-4">Mobile No</th>
              <th className="p-4">Roles</th>
              <th className="p-4">Created At</th>
              <th className="p-4 pr-6 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
            {activeUserList.length === 0 ? (
              <tr>
                <td colSpan="5" className="p-12 text-center text-gray-400 font-semibold">
                  No accounts found in this category.
                </td>
              </tr>
            ) : (
              displayedUserList.map(u => (
                <tr key={u.id} className="hover:bg-gray-50 transition-all border-b border-gray-100 last:border-0">
                  <td className="p-4 pl-6 text-left font-semibold text-gray-800">{u.name}</td>
                  <td className="p-4 text-left font-medium text-gray-700 break-all">{u.emailId}</td>
                  <td className="p-4 text-left font-mono">{u.mobileNo || 'N/A'}</td>
                  <td className="p-4 text-left">
                    <div className="flex flex-wrap gap-1.5">
                      {u.roles.map(r => (
                        <span key={r} className={`px-2 py-0.5 text-[9px] font-bold rounded uppercase ${r === 'ROLE_SUPER_ADMIN' ? 'bg-violet-50 text-violet-700 border border-violet-100' :
                          r === 'ROLE_SALES_ADMIN' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                            r === 'ROLE_LISTING_ADMIN' ? 'bg-orange-50 text-orange-700 border border-orange-100' :
                              r === 'ROLE_SUPPORT_ADMIN' ? 'bg-rose-50 text-rose-700 border border-rose-100' :
                                r === 'ROLE_MAGAZINE_SALES' ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' :
                                  'bg-gray-50 text-gray-600 border border-gray-200'
                          }`}>
                          {r.replace('ROLE_', '').replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-4 text-left whitespace-nowrap text-xs font-semibold text-gray-700">
                    {formatDateTime(u.createdAt)}
                  </td>
                  <td className="p-4 pr-6 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditUserRoles({ email: u.emailId, name: u.name, roles: u.roles })}
                        className="text-xs font-bold py-1.5 px-3 bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-700 rounded-lg transition-all active:scale-[0.98] cursor-pointer shadow-sm"
                      >
                        Edit Roles
                      </button>
                      <button
                        onClick={() => setChangePasswordUser({ email: u.emailId, name: u.name })}
                        className="text-xs font-bold py-1.5 px-3 bg-white border border-gray-200 hover:border-teal-100 hover:bg-teal-50/30 text-teal-600 hover:text-teal-700 rounded-lg transition-all active:scale-[0.98] cursor-pointer shadow-sm"
                      >
                        Change Password
                      </button>
                      <button
                        onClick={() => setDeleteUserConfirm({ id: u.id, email: u.emailId, name: u.name })}
                        className="text-xs font-bold py-1.5 px-3 bg-rose-50 border border-rose-100 hover:bg-rose-100 hover:border-rose-200 text-rose-600 hover:text-rose-700 rounded-lg transition-all active:scale-[0.98] cursor-pointer shadow-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-505 font-medium border-t border-gray-100 pt-5 mt-6 select-none bg-gray-50/40">
        <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
          {activeUserList.length === 0 ? (
            'No records available'
          ) : (
            `Showing ${safeUserPage * userPageSize + 1} to ${Math.min((safeUserPage + 1) * userPageSize, activeUserList.length)} of ${activeUserList.length} entries`
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
          <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
            <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Show</span>
            <div className="relative">
              <select
                value={userPageSize}
                onChange={(e) => {
                  setUserPageSize(Number(e.target.value));
                  setUserPage(0);
                }}
                className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                <ChevronDown size={12} />
              </span>
            </div>
            <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Entries</span>
          </div>

          {userTotalPages > 0 && (
            <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setUserPage(p => Math.max(0, p - 1))}
                disabled={safeUserPage === 0}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Previous
              </button>

              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers(safeUserPage, userTotalPages).map((pageNum, idx) => {
                  if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                    return (
                      <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                        ...
                      </span>
                    );
                  }
                  const isActive = safeUserPage === pageNum;
                  return (
                    <button
                      key={`page-${pageNum}`}
                      type="button"
                      onClick={() => setUserPage(pageNum)}
                      className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                        ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm min-w-[70px] text-center">
                {safeUserPage + 1} / {userTotalPages}
              </span>

              <button
                type="button"
                onClick={() => setUserPage(p => Math.min(userTotalPages - 1, p + 1))}
                disabled={safeUserPage >= userTotalPages - 1}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Admin Modal */}
      {showCreateAdminModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[85vh] my-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0 select-none">
              <h3 className="text-lg font-bold text-gray-800">Add Administrative Account</h3>
              <button onClick={() => setShowCreateAdminModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateAdminSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1 text-left">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Full Name</label>
                  <input
                    type="text"
                    required
                    placeholder="John Doe"
                    value={adminForm.name}
                    onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-800"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Email Address</label>
                  <input
                    type="email"
                    required
                    placeholder="johndoe@topinteriors.com"
                    value={adminForm.emailId}
                    onChange={(e) => setAdminForm({ ...adminForm, emailId: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-805"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Mobile Number (10 digits)</label>
                  <input
                    type="text"
                    required
                    pattern="[0-9]{10}"
                    placeholder="9876543210"
                    value={adminForm.mobileNo}
                    onChange={(e) => setAdminForm({ ...adminForm, mobileNo: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-805"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Password</label>
                  <input
                    type="password"
                    required
                    placeholder="Min. 8 characters"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none font-semibold text-gray-805"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Assign Roles</label>
                  <div className="grid grid-cols-2 gap-2.5 mt-2 bg-gray-50 p-3 rounded-xl border border-gray-200 text-xs select-none">
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('SUPER_ADMIN')}
                        onChange={() => handleRoleCheckboxChange('SUPER_ADMIN')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      Super Admin
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('SALES_ADMIN')}
                        onChange={() => handleRoleCheckboxChange('SALES_ADMIN')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      Sales Admin
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('LISTING_ADMIN')}
                        onChange={() => handleRoleCheckboxChange('LISTING_ADMIN')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      Listing Admin
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('SUPPORT_ADMIN')}
                        onChange={() => handleRoleCheckboxChange('SUPPORT_ADMIN')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      Support Admin
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                      <input
                        type="checkbox"
                        checked={adminForm.roles.includes('MAGAZINE_SALES')}
                        onChange={() => handleRoleCheckboxChange('MAGAZINE_SALES')}
                        className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                      />
                      Magazine Sales
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 p-6 bg-gray-50/50 flex-shrink-0 select-none">
                <button
                  type="button"
                  onClick={() => setShowCreateAdminModal(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-[#2ab391]/10 cursor-pointer"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Roles Modal */}
      {editUserRoles && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
          <div className="bg-white max-w-sm w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[85vh] my-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0 select-none">
              <div>
                <span className="text-[10px] uppercase font-bold text-gray-400">Modify Privileges</span>
                <h3 className="text-lg font-bold text-gray-800 mt-0.5">{editUserRoles.name}</h3>
              </div>
              <button onClick={() => setEditUserRoles(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleEditUserRolesSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1 text-left font-semibold text-gray-700">
                <p className="text-xs text-gray-400 font-medium select-none">Select roles below to change this user's platform permissions:</p>

                <div className="space-y-2.5 bg-gray-50 p-4 rounded-xl border border-gray-200 text-sm select-none">
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_USER')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_USER')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Standard User (ROLE_USER)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_VENDOR')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_VENDOR')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Vendor (ROLE_VENDOR)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_SUPER_ADMIN')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_SUPER_ADMIN')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Super Admin
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_SALES_ADMIN')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_SALES_ADMIN')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Sales Admin
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_LISTING_ADMIN')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_LISTING_ADMIN')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Listing Admin
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_SUPPORT_ADMIN')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_SUPPORT_ADMIN')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Support Admin
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={editUserRoles.roles.includes('ROLE_MAGAZINE_SALES')}
                      onChange={() => handleEditUserRoleCheckboxChange('ROLE_MAGAZINE_SALES')}
                      className="rounded text-[#2ab391] focus:ring-[#2ab391] w-4 h-4 cursor-pointer"
                    />
                    Magazine Sales (ROLE_MAGAZINE_SALES)
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 p-6 bg-gray-50/50 flex-shrink-0 select-none">
                <button
                  type="button"
                  onClick={() => setEditUserRoles(null)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-[#2ab391]/10 cursor-pointer"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {changePasswordUser && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
          <div className="bg-white max-w-sm w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[85vh] my-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0 select-none">
              <div>
                <span className="text-[10px] uppercase font-bold text-gray-400">Security Administration</span>
                <h3 className="text-lg font-bold text-gray-800 mt-0.5">Change Password</h3>
              </div>
              <button
                onClick={() => {
                  setChangePasswordUser(null);
                  setNewPassword('');
                  setConfirmNewPassword('');
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleChangePasswordSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1 text-left">
                <div className="bg-teal-50/50 border border-teal-100/50 rounded-xl p-3.5 text-xs text-teal-800 font-medium">
                  <p>Changing password for:</p>
                  <p className="font-bold text-sm text-teal-950 mt-1">{changePasswordUser.name}</p>
                  <p className="text-[11px] text-teal-700 font-semibold">{changePasswordUser.email}</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">New Password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="Min. 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none focus:ring-1 focus:ring-[#2ab391] font-semibold text-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1.5">Confirm New Password</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="Re-enter new password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-[#2ab391] focus:outline-none focus:ring-1 focus:ring-[#2ab391] font-semibold text-gray-800"
                  />
                </div>

                <p className="text-[10px] text-gray-400 font-medium leading-relaxed select-none">
                  Upon submission, the user's password will be updated instantly, and a password update email containing the new credentials will be sent to the user's registered email address.
                </p>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 p-6 bg-gray-50/50 flex-shrink-0 select-none">
                <button
                  type="button"
                  onClick={() => {
                    setChangePasswordUser(null);
                    setNewPassword('');
                    setConfirmNewPassword('');
                  }}
                  disabled={passwordLoading}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="px-4 py-2 bg-[#2ab391] hover:bg-[#23977a] disabled:bg-teal-300 text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-[#2ab391]/10 cursor-pointer"
                >
                  {passwordLoading ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {deleteUserConfirm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
          <div className="bg-white max-w-md w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[90vh] my-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-rose-50/50 flex-shrink-0 select-none">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-rose-500">Critical Action</span>
                  <h3 className="text-base font-extrabold text-rose-955 mt-0.5">Delete User Account</h3>
                </div>
              </div>
              <button
                onClick={() => {
                  setDeleteUserConfirm(null);
                  setDeleteInputConfirm('');
                }}
                className="text-gray-400 hover:text-gray-600 cursor-pointer p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleDeleteUserSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="p-6 space-y-4 overflow-y-auto flex-1 text-left">
                <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-xs text-rose-900 font-medium">
                  <p className="font-semibold text-rose-955 text-sm">Are you sure you want to delete this user?</p>
                  <p className="font-bold text-base text-rose-955 mt-2">{deleteUserConfirm.name}</p>
                  <p className="text-[11px] text-rose-700 font-semibold">{deleteUserConfirm.email}</p>
                </div>

                <div className="space-y-2">
                  <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">This will permanently delete:</p>
                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-gray-555 font-semibold leading-relaxed">
                    <li>The user account and platform access.</li>
                    <li>All listings, company profile logos, banners, proofs, and media files from the disk.</li>
                    <li>Review submissions, replies, review report flags, and customer inquiries.</li>
                    <li>Payment transaction history, active gateway/manual memberships.</li>
                    <li>Magazine subscription logs, print copy orders, and support tickets.</li>
                  </ul>
                </div>

                <div className="pt-2 border-t border-gray-100 space-y-3.5 text-left">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1.5">
                      To verify, type <span className="text-rose-600 font-extrabold">DELETE</span> below:
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Type DELETE"
                      value={deleteInputConfirm}
                      onChange={(e) => setDeleteInputConfirm(e.target.value)}
                      className="w-full bg-white border border-gray-200 rounded-lg p-2.5 text-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500 font-semibold text-gray-800"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 p-6 bg-gray-50/50 flex-shrink-0 select-none">
                <button
                  type="button"
                  onClick={() => {
                    setDeleteUserConfirm(null);
                    setDeleteInputConfirm('');
                  }}
                  disabled={deleteLoading}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={deleteLoading || deleteInputConfirm !== 'DELETE'}
                  className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300 text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all shadow-md shadow-rose-600/10 cursor-pointer"
                >
                  {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsersTab;
